import {
  DocumentDetail,
  type DocumentDetail as DocumentDetailData,
} from "@/components/accounting/document-detail";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { createFileRoute, notFound, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/accounting/invoices/$id` — one issued document.
 *
 * The only write on this page is the credit note, because an issued
 * document is never edited: a correction is a new, mirrored document
 * and it needs a stated reason.
 */

export const Route = createFileRoute("/_app/accounting/invoices/$id")({
  loader: async ({ params }) => {
    try {
      return await api.get<DocumentDetailData>(
        `/api/accounting/documents/${params.id}?side=issued`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: Page,
});

function Page() {
  const document = Route.useLoaderData();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const creditable =
    document.type === "invoice" &&
    document.status !== "cancelled" &&
    document.status !== "draft" &&
    (document.corrections?.length ?? 0) === 0;

  async function creditNote() {
    setBusy(true);
    try {
      await api.post(`/api/accounting/documents/${document.id}/credit-note`, { reason });
      toast({ title: "Credit note issued" });
      setOpen(false);
      setReason("");
      await router.invalidate();
    } catch (err) {
      toast({
        title: "Credit note failed",
        description: err instanceof Error ? err.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DocumentDetail
        document={document}
        side="issued"
        backTo="/accounting/invoices"
        backLabel="Invoices"
        actions={
          creditable ? (
            <Button variant="destructive" onClick={() => setOpen(true)}>
              Issue credit note
            </Button>
          ) : null
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue a credit note</DialogTitle>
            <DialogDescription>
              The invoice stays in the books and is cancelled by a mirrored, negative document. Say
              why — it is what an audit reads later.
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1 text-sm">
            <span className="text-muted-foreground">Reason</span>
            <input
              className="w-full rounded-md border bg-background px-3 py-2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Wrongly billed hours"
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={creditNote} disabled={busy || reason.trim().length < 3}>
              {busy ? "Issuing…" : "Issue credit note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
